import type { Embed } from '@tunasong/models'
import { isEmbed, isSpotify, isYouTube } from '@tunasong/models'
import { parseLink } from '@tunasong/plugin-embed/parse-link'
import type { Audio, Persisted, PollOptionAudio } from '@tunasong/schemas'

/** One piece of audio a linked entity (usually a song) offers for its poll option. */
export interface AudioCandidate {
  /** Stable identity for de-duplication and form values: `entity:<id>` or `external:<url>` */
  key: string
  audio: PollOptionAudio
  label: string
  /** Heading the candidate is listed under: "Hosted audio" or the service name */
  group: string
}

const hostedGroup = 'Hosted audio'

/** The identity candidates are de-duplicated and selected by. */
export const audioKey = (audio: PollOptionAudio) =>
  audio.kind === 'entity' ? `entity:${audio.entityId}` : `external:${audio.url}`

/** Display names, in ranking order. Unknown services rank after these. */
const services: Record<string, string> = {
  spotify: 'Spotify',
  youtube: 'YouTube',
  apple: 'Apple Music',
  tidal: 'Tidal',
  qobuz: 'Qobuz',
  soundcloud: 'SoundCloud',
}
/** "Spotify" for `spotify`; unknown services show as stored. */
export const serviceName = (service: string) => services[service] ?? service

const serviceRank = (service: string) => {
  const rank = Object.keys(services).indexOf(service)
  return rank === -1 ? Object.keys(services).length : rank
}

/**
 * A link `parseLink` understands and a person can open. An embed block stores the service's PLAYER url
 * (`open.spotify.com/embed/track/…`, `youtube.com/embed/…`), which `parseLink` does not accept back, so rebuild
 * the share link where the service allows it; otherwise keep what the embed has.
 */
export const toShareUrl = (embed: Embed) => {
  if (isSpotify(embed) && embed.uri) {
    const [, mediaType, mediaId] = embed.uri.split(':')
    if (mediaType && mediaId) {
      return `https://open.spotify.com/${mediaType}/${mediaId}`
    }
  }
  if (isYouTube(embed) && embed.mediaId) {
    return `https://www.youtube.com/watch?v=${embed.mediaId}`
  }
  return embed.url
}

const serviceOf = (candidate: AudioCandidate) => (candidate.audio.kind === 'external' ? candidate.audio.service : '')

const displayUrl = (url: string) => url.replace(/^https?:\/\/(www\.)?/, '')

const externalCandidate = (service: string, url: string): AudioCandidate => ({
  key: audioKey({ kind: 'external', service, url }),
  audio: { kind: 'external', service, url },
  label: displayUrl(url),
  group: serviceName(service),
})

const entityCandidate = (entityId: string, label: string): AudioCandidate => ({
  key: audioKey({ kind: 'entity', entityId }),
  audio: { kind: 'entity', entityId },
  label,
  group: hostedGroup,
})

interface DocNode {
  type?: unknown
  id?: unknown
  url?: unknown
  entityId?: unknown
  children?: unknown
}

const isDocNode = (node: unknown): node is DocNode => typeof node === 'object' && node !== null

/** Audio blocks, references to audio, embeds and media links, anywhere in the document. */
const collectFromNodes = (nodes: readonly unknown[], found: AudioCandidate[]) => {
  for (const node of nodes) {
    if (!isDocNode(node)) {
      continue
    }
    if (node.type === 'audio' && typeof node.id === 'string') {
      found.push(entityCandidate(node.id, 'Audio in the song'))
    } else if (node.type === 'ref' && typeof node.entityId === 'string') {
      /** A ref embeds a sanitised copy of the entity, so its first child says what it points at. */
      const [target] = Array.isArray(node.children) ? node.children : []
      if (isDocNode(target) && target.type === 'audio') {
        found.push(entityCandidate(node.entityId, 'Audio in the song'))
      }
      continue
    } else if (isEmbed(node)) {
      const url = toShareUrl(node)
      if (url) {
        found.push(externalCandidate(node.service, url))
      }
    } else if (node.type === 'link' && typeof node.url === 'string') {
      const embed = parseLink(node.url)
      if (embed) {
        found.push(externalCandidate(embed.service, toShareUrl(embed) ?? node.url))
      }
    }
    if (Array.isArray(node.children)) {
      collectFromNodes(node.children, found)
    }
  }
}

const isRecording = (clip: Persisted<Audio>) => clip.mediaType === 'recording'

/**
 * Every piece of audio a linked song offers, best first:
 * 1. hosted recordings, newest first; 2. other hosted audio, newest first; 3. external links by service
 *    (Spotify, YouTube, Apple Music, Tidal, Qobuz, SoundCloud, then others), in document order.
 *
 * `clips` are the song's child audio and `hasRecording` targets (`useEntityClips`); `nodes` is its document.
 * Each piece of audio appears once, however many ways the song points at it.
 */
export const findAudioCandidates = ({ clips, nodes }: { clips: Persisted<Audio>[]; nodes: readonly unknown[] }) => {
  const hosted = [...clips]
    .sort((a, b) =>
      isRecording(a) === isRecording(b) ? b.updatedAt.localeCompare(a.updatedAt) : isRecording(a) ? -1 : 1
    )
    .map(clip => entityCandidate(clip.id, clip.name || 'Untitled audio'))

  const fromDocument: AudioCandidate[] = []
  collectFromNodes(nodes, fromDocument)
  const hostedFromDocument = fromDocument.filter(c => c.audio.kind === 'entity')
  /** Array.prototype.sort is stable, so candidates of one service keep their document order. */
  const external = fromDocument
    .filter(c => c.audio.kind === 'external')
    .sort((a, b) => serviceRank(serviceOf(a)) - serviceRank(serviceOf(b)))

  const seen = new Set<string>()
  return [...hosted, ...hostedFromDocument, ...external].filter(candidate => {
    if (seen.has(candidate.key)) {
      return false
    }
    seen.add(candidate.key)
    return true
  })
}
